import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getFeatureSettings, updateFeatureSettings } from '../../api';
import { PageHeader, TableSkeleton } from '../../components/ui';
import { Settings2, Save } from 'lucide-react';
import toast from 'react-hot-toast';

const MODULE_META: Record<
  string,
  { title: string; description: string; showApproval?: boolean; optionKeys?: Array<{ key: string; label: string }> }
> = {
  reviews: {
    title: 'Reviews',
    description: 'User reviews on astrologer profiles.',
    showApproval: true,
  },
  astrologerApplications: {
    title: 'Astrologer applications',
    description: 'New join requests from prospective astrologers.',
    showApproval: true,
  },
  consultations: {
    title: 'Consultations',
    description: 'Chat / audio / video consultation flows.',
    showApproval: false,
  },
  wallet: {
    title: 'Wallet',
    description: 'User wallet recharge and balance features.',
    showApproval: false,
  },
  notifications: {
    title: 'Notifications',
    description: 'Push and in-app notification delivery.',
    showApproval: false,
  },
  emergency: {
    title: 'Emergency consultations',
    description: 'Priority sessions with admin-configured surcharge (PDF §43.1).',
    showApproval: false,
  },
  appointments: {
    title: 'Appointments',
    description: 'Scheduled consultation booking (PDF §12, §30).',
    showApproval: false,
  },
};

export default function FeatureSettingsPage() {
  const qc = useQueryClient();
  const [form, setForm] = useState<any>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-features'],
    queryFn: () => getFeatureSettings().then((r) => r.data.data.features),
  });

  useEffect(() => {
    if (data) setForm(JSON.parse(JSON.stringify(data)));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (features: any) => updateFeatureSettings(features),
    onSuccess: () => {
      toast.success('Feature settings saved');
      qc.invalidateQueries({ queryKey: ['admin-features'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const patchModule = (key: string, patch: any) => {
    setForm((prev: any) => ({
      ...prev,
      [key]: { ...prev[key], ...patch },
    }));
  };

  const patchOption = (mod: string, optKey: string, value: boolean) => {
    setForm((prev: any) => ({
      ...prev,
      [mod]: {
        ...prev[mod],
        options: { ...(prev[mod]?.options || {}), [optKey]: value },
      },
    }));
  };

  if (isLoading || !form) {
    return (
      <div className="p-6">
        <PageHeader title="Feature controls" subtitle="Enable modules and switch approval modes" />
        <TableSkeleton rows={6} cols={3} />
      </div>
    );
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Feature controls"
        subtitle="Activate modules, set manual vs auto approval, and tune content rules — no code changes required"
      >
        <button
          type="button"
          className="btn-primary"
          disabled={saveMutation.isPending}
          onClick={() => saveMutation.mutate(form)}
        >
          <Save size={14} />
          {saveMutation.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </PageHeader>

      <div className="space-y-4 max-w-3xl">
        {Object.keys(MODULE_META).map((key) => {
          const meta = MODULE_META[key];
          const mod = form[key] || { enabled: true, approvalMode: 'manual', options: {} };
          return (
            <div key={key} className="card p-5">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                    <Settings2 size={15} className="text-primary-500" />
                    {meta.title}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1">{meta.description}</p>
                </div>
                <label className="flex items-center gap-2 text-xs font-bold text-gray-700 shrink-0">
                  <input
                    type="checkbox"
                    checked={mod.enabled !== false}
                    onChange={(e) => patchModule(key, { enabled: e.target.checked })}
                  />
                  Enabled
                </label>
              </div>

              {meta.showApproval ? (
                <div className="mt-4">
                  <div className="text-[11px] font-semibold text-gray-500 mb-2">Approval mode</div>
                  <div className="flex gap-2">
                    {(['manual', 'auto'] as const).map((mode) => (
                      <button
                        key={mode}
                        type="button"
                        disabled={mod.enabled === false}
                        onClick={() => patchModule(key, { approvalMode: mode })}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold border capitalize ${
                          mod.approvalMode === mode
                            ? 'bg-primary-500 text-white border-primary-500'
                            : 'bg-white text-gray-600 border-gray-200'
                        } disabled:opacity-40`}
                      >
                        {mode === 'manual' ? 'Manual approval' : 'Auto approval'}
                      </button>
                    ))}
                  </div>
                  <p className="text-[11px] text-gray-400 mt-2">
                    {mod.approvalMode === 'auto'
                      ? 'New items go live immediately without admin review.'
                      : 'New items wait in the moderation queue until approved.'}
                  </p>
                </div>
              ) : null}

              {meta.optionKeys?.length ? (
                <div className="mt-4 flex flex-wrap gap-4">
                  {meta.optionKeys.map((opt) => (
                    <label key={opt.key} className="flex items-center gap-2 text-xs text-gray-700">
                      <input
                        type="checkbox"
                        checked={mod.options?.[opt.key] !== false}
                        disabled={mod.enabled === false}
                        onChange={(e) => patchOption(key, opt.key, e.target.checked)}
                      />
                      {opt.label}
                    </label>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
